import React, { useState, useRef, useCallback, useEffect } from "react";
import { AutoTaggingResult, ClothingItem } from "../../../utilities/types/unified-services";

interface AutoTaggingResultsProps {
  result: AutoTaggingResult;
  uploadedImage?: string;
}

interface ImageDimensions {
  width: number;
  height: number;
}

interface ScaleFactor {
  x: number;
  y: number;
}

const AutoTaggingResults: React.FC<AutoTaggingResultsProps> = ({ result, uploadedImage }) => {
  const [hoveredItem, setHoveredItem] = useState<ClothingItem | null>(null);
  const [activeItem, setActiveItem] = useState<ClothingItem | null>(null);
  const [selectedItem, setSelectedItem] = useState<ClothingItem | null>(null);
  const [imageDimensions, setImageDimensions] = useState<ImageDimensions | null>(null);
  const [scaleFactor, setScaleFactor] = useState<ScaleFactor | null>(null);
  const imageRef = useRef<HTMLImageElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const containerWidth = 240;
  const containerHeight = 400;

  useEffect(() => {
    if (result?.Clothes?.length && !activeItem) {
      setActiveItem(result.Clothes[0]);
    }
  }, [result, activeItem]);

  useEffect(() => {
    if (hoveredItem) {
      setActiveItem(hoveredItem);
    } else if (selectedItem) {
      setActiveItem(selectedItem);
    } else if (result?.Clothes?.length) {
      setActiveItem(result.Clothes[0]);
    }
  }, [hoveredItem, selectedItem, result]);

  const handleItemSelect = (item: ClothingItem) => {
    if (selectedItem === item) {
      setSelectedItem(null);
      setActiveItem(result.Clothes[0]);
    } else {
      setSelectedItem(item);
      setActiveItem(item);
    }
  };

  const handleImageLoad = useCallback(() => {
    if (imageRef.current) {
      const img = imageRef.current;
      const originalWidth = img.naturalWidth;
      const originalHeight = img.naturalHeight;

      const scale = Math.min(containerWidth / originalWidth, containerHeight / originalHeight);

      setImageDimensions({
        width: originalWidth,
        height: originalHeight,
      });

      setScaleFactor({
        x: scale,
        y: scale,
      });
    }
  }, []);

  const getAdjustedCoordinates = useCallback(
    (region: ClothingItem["Region"]) => {
      if (!imageDimensions || !scaleFactor) return region;

      const scaledWidth = imageDimensions.width * scaleFactor.x;
      const scaledHeight = imageDimensions.height * scaleFactor.y;
      const offsetX = (containerWidth - scaledWidth) / 2;
      const offsetY = (containerHeight - scaledHeight) / 2;

      return {
        X1: ((region.X1 * scaleFactor.x + offsetX) / containerWidth) * 100,
        Y1: ((region.Y1 * scaleFactor.y + offsetY) / containerHeight) * 100,
        X2: ((region.X2 * scaleFactor.x + offsetX) / containerWidth) * 100,
        Y2: ((region.Y2 * scaleFactor.y + offsetY) / containerHeight) * 100,
      };
    },
    [imageDimensions, scaleFactor]
  );

  const getCenter = (region: ClothingItem["Region"]) => {
    const { X1, Y1, X2, Y2 } = getAdjustedCoordinates(region);
    return {
      x: (X1 + X2) / 2,
      y: (Y1 + Y2) / 2,
    };
  };

  const getInverseClipPath = (region: ClothingItem["Region"]) => {
    const { X1, Y1, X2, Y2 } = getAdjustedCoordinates(region);
    const radius = 1;
    return `polygon(
      0% 0%,
      100% 0%,
      100% 100%,
      0% 100%,
      0% 0%,

      ${X1}% ${Y1 + radius}%,
      ${X1}% ${Y2 - radius}%,
      ${X1 + radius}% ${Y2}%,
      ${X2 - radius}% ${Y2}%,
      ${X2}% ${Y2 - radius}%,
      ${X2}% ${Y1 + radius}%,
      ${X2 - radius}% ${Y1}%,
      ${X1 + radius}% ${Y1}%,
      ${X1}% ${Y1 + radius}% 
    )`;
  };

  const formatClothingType = (clotheType: string): string => {
    return clotheType.replace(/([A-Z])/g, " $1").trim();
  };

  const PropertyChip: React.FC<{ label: string; value: string }> = ({ label, value }) => (
    <div
      className="flex items-center gap-3 px-4 py-2.5 transition-colors duration-200 
                    bg-gradient-to-r from-gray-50/40 to-blue-50/30 
                    hover:from-gray-50/60 hover:to-blue-50/50 
                    border border-gray-200 rounded-lg shadow-sm"
    >
      <span className="text-sm font-semibold text-gray-600">{label}:</span>
      <span className="text-sm text-gray-800 capitalize">{value}</span>
    </div>
  );

  const renderColorTag = (colorArray: any) => {
    const processColor = (color: any) => {
      if (typeof color === "string") {
        const colorValues = color
          .replace(/[\[\]]/g, "")
          .trim()
          .split(/\s+/)
          .filter((val) => val.trim() !== "")
          .map(Number)
          .filter((val) => !isNaN(val));

        if (colorValues.length >= 3) {
          const [r, g, b] = colorValues;
          return { r, g, b };
        }
        return null;
      } else if (Array.isArray(color) && color.length > 0 && typeof color[0] === "string") {
        const colorValues = color[0]
          .replace(/[\[\]]/g, "")
          .trim()
          .split(/\s+/)
          .filter((val) => val.trim() !== "")
          .map(Number)
          .filter((val) => !isNaN(val));

        if (colorValues.length >= 3) {
          const [r, g, b] = colorValues;
          return { r, g, b };
        }
        return null;
      }
      return null;
    };

    const renderSingleColor = (color: any, index: number) => {
      const processedColor = processColor(color);
      if (!processedColor) return null;

      const { r, g, b } = processedColor;
      return (
        <div
          key={index}
          className="flex items-center gap-3 px-4 py-2.5 bg-gradient-to-r from-[#FAFAED]/40 to-[#E3E4F8]/30 hover:from-[#FAFAED]/60 hover:to-[#E3E4F8]/50 border border-gorgonzola-blue-50/80 rounded-lg shadow-sm transition-all duration-200"
        >
          {/* <img src="/assets/image/free-trial/auto-tagging/sparkles.svg" alt="sparkles" className="w-5 h-5" /> */}
          <span className="text-xs font-semibold text-gorgonzola-blue-400">Color:</span>
          <div className="w-6 h-6 border-2 rounded-full shadow-inner border-white/80" style={{ backgroundColor: `rgb(${r}, ${g}, ${b})` }} />
        </div>
      );
    };

    if (Array.isArray(colorArray)) {
      return colorArray.map((color, index) => renderSingleColor(color, index));
    }
    return renderSingleColor(colorArray, 0);
  };

  const renderClothingCard = (item: ClothingItem, index: number) => {
    return (
      <div className="relative flex flex-col gap-5 p-4 transition-all duration-300 border border-gray-200 shadow-sm bg-gradient-to-br from-white via-white to-gray-50/10 rounded-xl">
        <div className="relative pb-4 border-b border-gray-200">
          <h3 className="text-lg font-bold text-center text-gray-800">{formatClothingType(item.ClotheType)}</h3>
          <div className="absolute bottom-0 w-16 h-px transform -translate-x-1/2 left-1/2 bg-gradient-to-r from-transparent via-primary to-transparent" />
        </div>

        <div className="grid gap-4">
          <div className="flex flex-wrap gap-2">
            {Object.entries(item).map(([key, value]) => {
              if (!value || key === "Colors" || key === "Region" || key === "ClotheType") return null;
              return (
                <div key={key} className="w-fit">
                  <PropertyChip label={key} value={value as string} />
                </div>
              );
            })}
          </div>

          {item.Colors?.length > 0 && <div className="flex flex-wrap gap-2">{item.Colors.map((color, idx) => color && renderColorTag(color))}</div>}
        </div>
      </div>
    );
  };

  const renderClothesList = () => {
    if (!result?.Clothes) return null;

    return (
      <div className="flex flex-wrap gap-2">
        {result.Clothes.map((item, index) => (
          <button
            key={index}
            className={`px-4 py-2 text-sm font-medium transition-all duration-200 border rounded-lg
              ${
                selectedItem === item
                  ? "border-primary bg-primary text-white"
                  : activeItem === item
                  ? "border-primary bg-primary/10 text-primary"
                  : "border-gray-200 bg-white text-gray-700 hover:border-primary/50 hover:bg-primary/5"
              }`}
            // onMouseEnter={() => setHoveredItem(item)}
            // onMouseLeave={() => setHoveredItem(null)}
            onClick={() => handleItemSelect(item)}
          >
            {formatClothingType(item.ClotheType)}
          </button>
        ))}
      </div>
    );
  };

  return (
    <div className="w-full space-y-8">
      <div className="flex flex-col items-center w-full gap-4">
        <div className="flex flex-col items-center w-full gap-4 md:items-start md:flex-row">
          <div className="space-y-4">
            <div className="p-4 bg-white border border-gray-200 shadow-sm rounded-2xl">
              <div ref={containerRef} className="relative mx-auto w-fit">
                <div className="grid place-content-center w-[15rem] h-[25rem] border rounded-xl border-gray-200 overflow-hidden bg-gray-50">
                  {uploadedImage ? (
                    <img
                      ref={imageRef}
                      src={uploadedImage.startsWith("data:") ? uploadedImage : `data:image/jpeg;base64,${uploadedImage}`}
                      alt="Analyzed clothing"
                      className="object-contain w-[15rem] h-[25rem]"
                      onLoad={handleImageLoad}
                    />
                  ) : (
                    <div className="grid size-full place-content-center">
                      <div className="text-center text-gray-500">
                        <svg className="w-16 h-16 mx-auto mb-2 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        <p className="text-sm">No image available</p>
                      </div>
                    </div>
                  )}
                </div>

                {result.Clothes.map((item, index) => {
                  const center = getCenter(item.Region);
                  const { X1, Y1, X2, Y2 } = getAdjustedCoordinates(item.Region);
                  const isActiveItem = selectedItem === item || hoveredItem === item;

                  return (
                    <div key={index}>
                      {!hoveredItem && !selectedItem && (
                        <div
                          className="absolute border-2 border-white rounded-full shadow-lg cursor-pointer opacity-70 bg-primary animate-pulse"
                          style={{
                            left: `${center.x}%`,
                            top: `${center.y}%`,
                            width: "24px",
                            height: "24px",
                            transform: "translate(-50%, -50%)",
                          }}
                          onMouseEnter={() => setHoveredItem(item)}
                          onClick={() => handleItemSelect(item)}
                        />
                      )}

                      {isActiveItem && (
                        <div
                          className={`absolute rounded-lg ring-2 bg-opacity-30 cursor-pointer ring-primary bg-primary`}
                          style={{
                            left: `${X1}%`,
                            top: `${Y1}%`,
                            width: `${X2 - X1}%`,
                            height: `${Y2 - Y1}%`,
                          }}
                          onMouseLeave={() => setHoveredItem(null)}
                          onClick={() => handleItemSelect(item)}
                        />
                      )}
                    </div>
                  );
                })}

                {(hoveredItem || selectedItem) && (
                  <div
                    className="absolute inset-0 bg-black pointer-events-none bg-opacity-40 rounded-xl"
                    style={{
                      clipPath: getInverseClipPath((selectedItem || hoveredItem)!.Region),
                    }}
                  />
                )}
              </div>
            </div>
          </div>

          <div className="flex-1 w-full">
            {result?.Clothes.length ? (
              <div className="flex flex-col">
                <div className="p-4 mb-6 bg-white border border-gray-200 shadow-sm rounded-2xl">{renderClothesList()}</div>

                {activeItem && (
                  <div className="bg-white border border-gray-200 shadow-sm rounded-2xl">
                    <div className="p-4">{renderClothingCard(activeItem, result.Clothes.indexOf(activeItem))}</div>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutoTaggingResults;
